import React, { useEffect, useRef, useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { PageContainer, Poppins, Spacer, TableSkeleton } from 'src/common';
import Button from 'src/components/form/Button';
import { useAuth } from 'src/state/auth';
import { Invites, Requests } from './comps';
import { OrganisationCreateModalV2, WorkspaceCreateModal } from 'src/components/modals';
import { useCache } from 'src/graphql/useCache';
import OrgTable from 'src/components/OrgTable';
import Input from 'src/components/form/Input';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { motion } from 'framer-motion';
import Loader from 'src/components/Loader/Loader';
import { useFlatOrganisationsHomeQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';

const Div = styled(PageContainer)`
  .head {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;

    &__buttons {
      display: flex;
      gap: 14px;
      grid-gap: 24px;
      align-items: center;

      &__switch {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: auto;
        cursor: pointer;
        user-select: none;
        margin-right: 10px;
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .head {
      &__buttons {
        flex-direction: column;
        gap: 14px;
        &__switch {
          margin-right: 0;
        }
      }
    }
  }
`;

const OrganisationHierarchyPage = () => {
  const [isOrganisationCreateModal, setIsOrganisationCreateModal] = useState(false);
  const [isWorspaceCreateModal, setIsWorspaceCreateModal] = useState(false);
  const [showArchived, setShowArchived] = useLocalStorage('showArchived', false);
  const [isRefetching, setIsRefetching] = useState(false);
  const hasFetchedArchived = useRef(false);
  const { profile } = useAuth();
  const { refetchQuery } = useCache();
  useGaEventPageView('Cydea | Organisations');

  const {
    data: flatOrgsData,
    loading,
    refetch: refetchFlatOrgs,
    networkStatus,
  } = useFlatOrganisationsHomeQuery({ variables: { memberEmail: profile?.user?.email } });
  const isFlatOrgsLoading = isQueryLoading({ loading, data: flatOrgsData, networkStatus });

  const flatOrganisations = flatOrgsData?.flatOrganisations || [];

  useEffect(() => {
    if (!showArchived || hasFetchedArchived.current) return;
    hasFetchedArchived.current = true;
    setIsRefetching(true);
    refetchFlatOrgs({ includeArchived: true })?.finally(() => setIsRefetching(false));
  }, [showArchived, refetchFlatOrgs]);

  const resetCache = () => refetchQuery({ include: ['profile', 'flatOrganisationsHome', 'flatOrganisationsNav'] });

  const canCreateChild = profile?.permissions.canCreateChild;

  return (
    <>
      <Meta title={`Cydea | Organisations`} feedbackScript />
      {isOrganisationCreateModal && <OrganisationCreateModalV2 onClose={() => setIsOrganisationCreateModal(false)} />}
      {isWorspaceCreateModal && <WorkspaceCreateModal onClose={() => setIsWorspaceCreateModal(false)} />}
      <Div>
        <div className="head">
          <Poppins px={32} weight={600}>
            Organisations
          </Poppins>

          <div className="head__buttons">
            <div
              className="head__buttons__switch"
              onClick={() => !isRefetching && setShowArchived(!showArchived)}
              style={{
                cursor: isRefetching ? 'not-allowed' : undefined,
                opacity: isRefetching ? 0.7 : undefined,
                transition: 'opacity 0.25s ease',
              }}
            >
              <Input checked={showArchived} className="switch" type="checkbox" disabled={isRefetching} />
              <Poppins color="davysGrey">Show archived workspaces</Poppins>
              <motion.span
                animate={isRefetching ? { opacity: 1, width: 20 } : { opacity: 0, width: 0 }}
                initial={false}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{ overflow: 'hidden', display: 'inline-flex', flexShrink: 0 }}
              >
                <Loader size="xs" />
              </motion.span>
            </div>
            {(profile?.permissions.canCreateOrganisation || canCreateChild) && (
              <Button
                secondary
                onClick={() => setIsOrganisationCreateModal(true)}
                $constWidth={300}
                data-cy="org-create-btn"
              >
                + CREATE ORGANISATION
              </Button>
            )}
            {(profile?.permissions.canCreateWorkspace || canCreateChild) && (
              <Button secondary onClick={() => setIsWorspaceCreateModal(true)} $constWidth={300}>
                + CREATE WORKSPACE
              </Button>
            )}
          </div>
        </div>

        <Invites onSuccess={resetCache} />
        <Requests onSuccess={resetCache} />
        <Spacer $px={32} />
        {isFlatOrgsLoading ? <TableSkeleton /> : <OrgTable data={flatOrganisations} showArchived={showArchived} />}
      </Div>
    </>
  );
};

export default OrganisationHierarchyPage;
